import { PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import toast from 'react-hot-toast';
import { isSufeExamClient } from '@/utils/examClient';
import { getExamInfo } from '@/api/start';
import type { IGetExamInfo } from '@/api/start';
import i18n from '@/locales/init';

type MockCourse = 'listening' | 'reading' | 'writing';

const isDoing = (status?: number) => status === 1 || status === 2;
const isCompleted = (status?: number) => status === 4;

const canOpenCourse = (exam: IGetExamInfo, course: MockCourse) => {
  if (exam.type !== 1) return false;
  if (course === 'listening') return isDoing(exam.listening.status);
  if (course === 'reading') return isCompleted(exam.listening.status) && isDoing(exam.reading.status);
  return isCompleted(exam.listening.status) && isCompleted(exam.reading.status) && isDoing(exam.writing.status);
};

export default function ClientExamGuard({course, children}: PropsWithChildren<{course: MockCourse;}>) {
  const {recordId} = useParams();
  const navigate = useNavigate();
  const restricted = isSufeExamClient();
  const [allowedRecordId, setAllowedRecordId] = useState<string>();

  useEffect(() => {
    if (!restricted) return;
    let active = true;
    setAllowedRecordId(undefined);
    void getExamInfo({record_id: Number(recordId)}).then(response => {
      if (!active) return;
      const exam = response.data;
      if (response.code === 200 && exam?.record_id === Number(recordId) && canOpenCourse(exam, course)) {
        setAllowedRecordId(recordId);
      } else {
        navigate(`/mockstart/${recordId}`, {replace: true});
      }
    }).catch(() => {
      if (!active) return;
      toast.error(i18n.t('common.dauyan_network_error_retry_later'));
      navigate(`/mockstart/${recordId}`, {replace: true});
    });
    return () => { active = false; };
  }, [course, navigate, recordId, restricted]);

  return <>{!restricted || allowedRecordId === recordId ? children : null}</>;
}
